import React, { useState, useEffect } from 'react';
import { getApiClient } from '../../api/apiClient';
import { toast } from 'react-toastify';
import { 
  XMarkIcon,
  CogIcon,
  ClockIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const EditShopConfigModal = ({ isOpen, onClose, onSuccess, shop, currentConfig }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    shopId: '',
    startTime: '08:00',
    endTime: '17:00',
    onlineAppointments: true,
    profilePic: '',
    description: '',
    calendarInterval: 'HOUR',
    shop_notification: 'EMAIL',
    workingHours: []
  });

  const daysOfWeek = [
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' },
    { value: 0, label: 'Sunday' }
  ];

  const calendarIntervals = [
    { value: 'HOUR', label: '1 Hour' },
    { value: 'HALF_HOUR', label: '30 Minutes' },
  ];

  const notificationTypes = [
    { value: 'EMAIL', label: 'Email' },
    { value: 'SMS', label: 'SMS' },
    { value: 'NONE', label: 'None' }
  ];

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen && shop && currentConfig) {
      setFormData({
        shopId: shop.id,
        startTime: currentConfig.startTime || '08:00',
        endTime: currentConfig.endTime || '17:00',
        onlineAppointments: currentConfig.onlineAppointments !== undefined ? currentConfig.onlineAppointments : true,
        profilePic: currentConfig.profilePic || '',
        description: currentConfig.description || '',
        calendarInterval: currentConfig.calendarInterval || 'HOUR',
        shop_notification: currentConfig.shop_notification || 'EMAIL',
        workingHours: currentConfig.workingHours || []
      });
    }
  }, [isOpen, shop, currentConfig]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWorkingHour = () => {
    setFormData(prev => ({
      ...prev,
      workingHours: [
        ...prev.workingHours,
        { dayOfWeek: 1, startTime: '08:00', endTime: '17:00' }
      ]
    }));
  };

  const removeWorkingHour = (index) => {
    setFormData(prev => ({
      ...prev,
      workingHours: prev.workingHours.filter((_, i) => i !== index)
    }));
  };

  const updateWorkingHour = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: prev.workingHours.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const validateForm = () => {
    if (!formData.shopId) {
      toast.error('Shop ID is required');
      return false;
    }

    // Validate time format
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.startTime) || !timeRegex.test(formData.endTime)) {
      toast.error('Please enter valid time format (HH:mm)');
      return false;
    }

    // Validate start time is before end time
    if (formData.startTime >= formData.endTime) {
      toast.error('Start time must be before end time');
      return false;
    }

    // Validate working hours
    for (let i = 0; i < formData.workingHours.length; i++) {
      const wh = formData.workingHours[i];
      if (!timeRegex.test(wh.startTime) || !timeRegex.test(wh.endTime)) {
        toast.error(`Invalid time format in working hours row ${i + 1}`);
        return false;
      }
      if (wh.startTime >= wh.endTime) {
        toast.error(`Start time must be before end time in working hours row ${i + 1}`);
        return false;
      }
    }

    // Validate profile picture URL if provided
    if (formData.profilePic && !isValidUrl(formData.profilePic)) {
      toast.error('Please enter a valid profile picture URL');
      return false;
    }

    return true;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const client = getApiClient();
      await client.post('/admin/shop/set-config', formData);

      toast.success('Shop configuration updated successfully!');
      onSuccess(); // Refresh the configuration
      onClose();
      
    } catch (error) {
      console.error('Error updating shop configuration:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update configuration';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-5 mx-auto p-6 border w-11/12 md:w-4/5 lg:w-3/4 max-w-4xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <CogIcon className="h-6 w-6 text-purple-600 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">
              Edit Shop Configuration
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Shop Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <h4 className="text-sm font-medium text-blue-800 mb-2">Shop Information</h4>
          <div className="text-sm text-blue-700">
            <p><strong>Brand Name:</strong> {shop?.name}</p>
            <p><strong>Shop ID:</strong> {shop?.id}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold text-gray-800 border-b pb-2">
                Basic Settings
              </h4>

              {/* Working Hours */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Time *
                  </label>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="time"
                      required
                      value={formData.startTime}
                      onChange={(e) => handleInputChange('startTime', e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Time *
                  </label>
                  <div className="relative">
                    <ClockIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <input
                      type="time"
                      required
                      value={formData.endTime}
                      onChange={(e) => handleInputChange('endTime', e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              {/* Online Appointments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Online Appointments
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.onlineAppointments}
                    onChange={(e) => handleInputChange('onlineAppointments', e.target.checked)}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label className="ml-2 block text-sm text-gray-900">
                    Allow customers to book appointments online
                  </label>
                </div>
              </div>

              {/* Calendar Interval */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Calendar Interval
                </label>
                <select
                  value={formData.calendarInterval}
                  onChange={(e) => handleInputChange('calendarInterval', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  disabled={loading}
                >
                  {calendarIntervals.map((interval) => (
                    <option key={interval.value} value={interval.value}>
                      {interval.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shop Notification */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Method
                </label>
                <select
                  value={formData.shop_notification}
                  onChange={(e) => handleInputChange('shop_notification', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  disabled={loading}
                >
                  {notificationTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              <h4 className="text-lg font-semibold text-gray-800 border-b pb-2">
                Profile & Description
              </h4>

              {/* Profile Picture */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Picture URL
                </label>
                <div className="relative">
                  <PhotoIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input
                    type="url"
                    value={formData.profilePic}
                    onChange={(e) => handleInputChange('profilePic', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    placeholder="https://example.com/profile.jpg"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  rows={4}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Professional auto repair service..."
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          {/* Working Hours Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="text-lg font-semibold text-gray-800">
                Detailed Working Hours
              </h4>
              <button
                type="button"
                onClick={addWorkingHour}
                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                disabled={loading}
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Day
              </button>
            </div>

            <div className="space-y-3">
              {formData.workingHours.map((workingHour, index) => (
                <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-md">
                  <select
                    value={workingHour.dayOfWeek}
                    onChange={(e) => updateWorkingHour(index, 'dayOfWeek', parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  >
                    {daysOfWeek.map((day) => (
                      <option key={day.value} value={day.value}>
                        {day.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="time"
                    value={workingHour.startTime}
                    onChange={(e) => updateWorkingHour(index, 'startTime', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  />

                  <span className="text-gray-500">to</span>

                  <input
                    type="time"
                    value={workingHour.endTime}
                    onChange={(e) => updateWorkingHour(index, 'endTime', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    disabled={loading}
                  />

                  <button
                    type="button"
                    onClick={() => removeWorkingHour(index)}
                    className="inline-flex items-center p-2 border border-transparent rounded-md text-red-600 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    disabled={loading}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {formData.workingHours.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                  No detailed working hours set. Click "Add Day" to add specific schedules.
                </div>
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Updating...
                </div>
              ) : (
                'Update Configuration'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditShopConfigModal;